import {defineStore} from 'pinia';
import {useEnvironmentStore} from '@internalDevportal/store/EnvironmentStore';
import snackbarService from '@core/services/SnackbarService';
import {
  Application,
  useApplicationStore,
} from '@internalDevportal/store/Environment-ApplicationStore';
import {SavedOptions, Options} from '@core/helpers/persistOptionsHelper';
import {Spec} from './SpecStore';
import {AxiosRequestConfigLoader} from '@core/store/LoaderStore';
import {setLimitAndSkipFilterOptions} from '@core/helpers/servicesHelper';
import {Company} from '@core/types/Company';
import {ref} from 'vue';
import ApiService from '@core/services/ApiService';
import {getErrorMessage} from '@core/utils/getErrorMessage';

export interface ApiBackendTeam {
  productId: string;
  backendTeamId: string;
}

export interface SubProduct {
  name: string;
  quota: string;
  id: string;
  displayName: string;
  quotaInterval: string;
  quotaTimeUnit: string;
  rateLimitInterval: string;
  rateLimitTimeUnit: string;
  spec: Spec;
  tags: string[];
  get: boolean;
  post: boolean;
  put: boolean;
  patch: boolean;
  delete: boolean;
}

export interface CategoryProduct {
  spec?: Spec;
  quota?: string;
  rateLimit?: string;
  quotaInterval: string;
  quotaTimeUnit: string;
  rateLimitInterval: string;
  rateLimitTimeUnit: string;
  get: boolean;
  post: boolean;
  put: boolean;
  patch: boolean;
  delete: boolean;
  id: string;
  categoryName: string;
  subProducts: SubProduct[];
  displayName: string;
  tags: string[];
}

export interface ApiProduct {
  id: string;
  displayName: string;
  apiproduct: string;
  plan: string;
  status: string;
  spec?: Spec;
  name?: string;
  productSpec?: Spec;
}

export interface PendingApiProduct {
  application: {
    displayName: string;
    id: string;
    name: string;
    developer: string;
  };
  credentials: {
    approve: string;
    reject: string;
    consumerKey: string;
    pendingProducts: string[];
  }[];
  environment: {
    id: string;
    displayName: string;
    name: string;
  };
}

interface Search {
  tags?: string[];
  name?: string;
}

export function getLinkedProducts(application: Application) {
  return application.credentials
    .filter(credential => credential.status === 'approved')
    .flatMap(credential => credential.apiProducts);
}

export const useApiProductStore = defineStore(
  'ApiProductStore',
  () => {
    const api = ApiService.getApiInstance();
    const environmentStore = useEnvironmentStore();
    const applicationStore = useApplicationStore();

    const apiproducts = ref<ApiProduct[]>([]);
    const categoryProducts = ref<CategoryProduct[]>([]);
    const apiproductsWithoutSpec = ref<ApiProduct[]>([]);
    const loadingCategories = ref<boolean>(false);
    const categoriesLoadError = ref<boolean>(false);
    const loadingApiproducts = ref<boolean>(false);
    const pendingApiproducts = ref<PendingApiProduct[]>([]);
    const productsPerPage = ref<number>(10);
    const searchProductArray = ref<Search>({});
    const appsPerPage = ref<number>(5);
    const newOptionsPending = ref<SavedOptions>({
      page: 1,
      itemsPerPage: 10,
      sortBy: [],
      groupBy: [],
      multiSort: false,
      mustSort: false,
      filter: {},
    });
    const newOptions = ref<SavedOptions>({
      page: 1,
      itemsPerPage: 10,
      sortBy: [],
      sortDesc: [],
      groupBy: [],
      multiSort: false,
      mustSort: false,
      filter: {},
    });
    const apiSpecOptions = ref<SavedOptions>({
      page: 1,
      itemsPerPage: 10,
      sortBy: [],
      groupBy: [],
      multiSort: false,
      mustSort: false,
      filter: {},
    });
    const apiProductsOptionsToAddRemove = ref<SavedOptions>({
      page: 1,
      itemsPerPage: 10,
      sortBy: [],
      groupBy: [],
      multiSort: false,
      mustSort: false,
      filter: {},
    });
    const catItemsPerPage = ref<number>(10);
    const catPage = ref<number>(1);
    const toggleSearchText = ref<string>('');
    const toggleSearch = ref<number | undefined>(undefined);
    const newSearchApp = ref<string>('');
    const newSelectedCompany = ref<Company | undefined>(undefined);
    const selectedItem = ref<PendingApiProduct | undefined>(undefined);
    const newPage = ref<number>(1);
    const totalProducts = ref<number>(0);
    const tags = ref<string[]>([]);
    const newSearchApiProductsToAddRemove = ref<string>('');

    function displayLinkedProducts(application: Application) {
      return getLinkedProducts(application)
        .map(
          (product: ApiProduct) => `${product.apiproduct}: ${product.status}`,
        )
        .join('<br/>');
    }

    function doesProductExist(productId: string): boolean {
      return apiproducts.value.some(apiproduct => apiproduct.id === productId);
    }

    function getProductDisplayName(productId: string): string {
      const product = apiproducts.value.find(
        apiproduct => apiproduct.id === productId,
      );
      return product ? product.displayName : productId;
    }

    function getProductVersion(productId: string): string | undefined {
      const product = apiproducts.value.find(
        apiproduct => apiproduct.id === productId,
      );
      return product?.spec?.spec?.info?.version;
    }

    function getProducts() {
      return apiproducts.value;
    }

    function getCategoryProducts() {
      return categoryProducts.value;
    }

    async function getApiproductsWithoutSpec() {
      if (!environmentStore.currentEnvironment?.id) {
        return;
      }
      loadingApiproducts.value = true;
      try {
        const response = await api.get(
          `/environments/${environmentStore.currentEnvironment.id}/apiproducts`,
        );
        apiproductsWithoutSpec.value = response.data.filter(
          (apiproduct: ApiProduct) => !apiproduct.spec,
        );
      } finally {
        loadingApiproducts.value = false;
      }
    }

    async function savePagePending(options: Options) {
      newOptionsPending.value.page = options.page ?? 1;
      newOptionsPending.value.itemsPerPage = options.itemsPerPage ?? 10;
    }

    async function getApiproducts() {
      if (!environmentStore.currentEnvironment?.id) {
        return;
      }
      loadingApiproducts.value = true;
      try {
        const response = await api.get(
          `/environments/${environmentStore.currentEnvironment.id}/apiproducts?spec=true`,
        );
        apiproducts.value = response.data;
      } finally {
        loadingApiproducts.value = false;
      }
    }

    async function getCatProducts(
      itemsPerPage?: number,
      search?: {name?: string; tags?: string[]},
      consumed?: string | undefined,
    ) {
      if (!environmentStore.currentEnvironment?.id) {
        return;
      }
      loadingCategories.value = true;
      categoriesLoadError.value = false;
      try {
        const options = {
          page: 1,
          itemsPerPage: 10,
        };
        options.page = catPage.value;
        if (itemsPerPage) {
          options.itemsPerPage = itemsPerPage;
        }
        const filterObjectCat = setLimitAndSkipFilterOptions({}, options);
        const queryParamsCat = `?filter=${encodeURI(JSON.stringify(filterObjectCat))}`;
        const catSearch =
          search && (search.name || search.tags)
            ? `&search=${encodeURI(JSON.stringify(search))}`
            : '';
        const categoryParams = queryParamsCat === '?' ? '' : queryParamsCat;
        const response = await api.get(
          `/environments/${environmentStore.currentEnvironment.id}/categoryproducts${categoryParams}${catSearch}&spec=true&consumed=${consumed}`,
        );
        categoryProducts.value = response.data.all;
        totalProducts.value = response.data.total;
        tags.value = response.data.tags;
      } catch (error) {
        categoriesLoadError.value = true;
        categoryProducts.value = [];
        totalProducts.value = 0;
        snackbarService.showSadSnackbar(getErrorMessage(error));
      } finally {
        loadingCategories.value = false;
      }
    }

    async function getPendingApiproducts() {
      if (!environmentStore.currentEnvironment?.id) {
        return;
      }
      try {
        const response = await api.get('/apiproducts/pending', {
          showLoader: false,
        } as AxiosRequestConfigLoader);
        pendingApiproducts.value = response?.data;
        selectedItem.value = response?.data ? response.data[0] : undefined;
      } catch (error) {
        snackbarService.showSadSnackbar(getErrorMessage(error));
        pendingApiproducts.value = [];
        selectedItem.value = undefined;
      }
    }

    async function approveOrRejectPendingApiproducts(data: {
      id: string;
      name: string;
      url: string;
    }) {
      if (!environmentStore.currentEnvironment?.id) {
        return;
      }
      const pendingProduct = pendingApiproducts.value.find(
        p => p.application.id === data.id,
      );
      if (!pendingProduct) {
        return;
      }

      const response = await api.post(`${data.url}`, [data.name]);
      if (response.status === 204) {
        const action = data.url.includes('approve') ? 'approved' : 'rejected';
        if (pendingProduct.credentials.length === 0) {
          return;
        }
        const nameIndex = pendingProduct.credentials[0].pendingProducts.indexOf(
          data.name,
        );
        if (nameIndex !== -1) {
          pendingProduct.credentials[0].pendingProducts.splice(nameIndex, 1);
        }
        if (pendingProduct.credentials[0].pendingProducts.length === 0) {
          const productIndex = pendingApiproducts.value.findIndex(
            p => p.application.id === data.id,
          );
          if (productIndex !== -1) {
            pendingApiproducts.value.splice(productIndex, 1);
          }
        }
        snackbarService.showHappySnackbar(
          `Successfully ${action} ${data.name}`,
        );
      }
    }

    async function saveProducts({
      id,
      add,
      remove,
      key,
    }: {
      id: string;
      add: string[];
      remove: string[];
      key: string;
    }) {
      if (!environmentStore.currentEnvironment?.id) {
        return;
      }
      const response = await api.post(
        `/environments/${environmentStore.currentEnvironment.id}/applications/${id}/keys/${key}`,
        {
          add,
          remove,
        },
      );
      await applicationStore.getApplication(id);
      return response;
    }

    async function assignProductToBackendTeam(
      productId: string,
      backendTeamId: string | null,
    ) {
      await api.post(
        `/environments/${environmentStore.currentEnvironment.id}/apiproducts/${productId}/assign-backend-team`,
        {
          backendTeamId: backendTeamId,
        },
      );
    }

    async function unassignProductBackendTeam(productId: string) {
      await assignProductToBackendTeam(productId, null);
    }

    function $reset() {
      apiproducts.value = [];
      categoryProducts.value = [];
      apiproductsWithoutSpec.value = [];
      loadingCategories.value = false;
      categoriesLoadError.value = false;
      loadingApiproducts.value = false;
      pendingApiproducts.value = [];
      productsPerPage.value = 10;
      searchProductArray.value = {};
      appsPerPage.value = 5;
      newOptionsPending.value = {
        page: 1,
        itemsPerPage: 10,
        sortBy: [],
        groupBy: [],
        multiSort: false,
        mustSort: false,
        filter: {},
      };
      newOptions.value = {
        page: 1,
        itemsPerPage: 10,
        sortBy: [],
        sortDesc: [],
        groupBy: [],
        multiSort: false,
        mustSort: false,
        filter: {},
      };
      apiSpecOptions.value = {
        page: 1,
        itemsPerPage: 10,
        sortBy: [],
        groupBy: [],
        multiSort: false,
        mustSort: false,
        filter: {},
      };
      apiProductsOptionsToAddRemove.value = {
        page: 1,
        itemsPerPage: 10,
        sortBy: [],
        groupBy: [],
        multiSort: false,
        mustSort: false,
        filter: {},
      };
      catItemsPerPage.value = 10;
      catPage.value = 1;
      toggleSearchText.value = '';
      toggleSearch.value = undefined;
      newSearchApp.value = '';
      newSelectedCompany.value = undefined;
      selectedItem.value = undefined;
      newPage.value = 1;
      totalProducts.value = 0;
      newSearchApiProductsToAddRemove.value = '';
    }

    return {
      apiproducts,
      categoryProducts,
      apiproductsWithoutSpec,
      loadingCategories,
      categoriesLoadError,
      loadingApiproducts,
      pendingApiproducts,
      productsPerPage,
      searchProductArray,
      appsPerPage,
      newOptionsPending,
      newOptions,
      apiSpecOptions,
      apiProductsOptionsToAddRemove,
      catItemsPerPage,
      catPage,
      toggleSearchText,
      toggleSearch,
      newSearchApp,
      newSelectedCompany,
      selectedItem,
      newPage,
      totalProducts,
      tags,
      newSearchApiProductsToAddRemove,
      getLinkedProducts,
      displayLinkedProducts,
      doesProductExist,
      getProductDisplayName,
      getProductVersion,
      getProducts,
      getCategoryProducts,
      getApiproductsWithoutSpec,
      savePagePending,
      getApiproducts,
      getCatProducts,
      getPendingApiproducts,
      approveOrRejectPendingApiproducts,
      saveProducts,
      assignProductToBackendTeam,
      unassignProductBackendTeam,
      $reset,
    };
  },
  {
    persist: {
      pick: [
        'newOptions',
        'newOptionsPending',
        'apiSpecOptions',
        'newSearchApp',
        'newSelectedCompany',
        'searchProductArray',
        'catItemsPerPage',
        'catPage',
        'toggleSearchText',
        'toggleSearch',
        'totalProducts',
        'apiproducts',
      ],
    },
  },
);
